import { InputView, LanguageIcon, UiUtils, HocElDivider } from 'ui-components'
import classNames from 'classnames'
import { ElPopover, ElSpace } from 'element-plus'
import { useAssetsStore, useBaseInfoStore, useLanguageStore, useStaticTableDataStore, useUserInfoStore, type CurrentCurrency } from 'store'
import { useI18n } from 'i18n-utils'
import { getLangName } from '@/constants/ElementPlusLangConfig.ts'
import './ChangeLanguageCurrency.scss'

interface ChangeLanguageCurrencyProps {
  isShowIcon?: boolean
}

export const ChangeLanguageCurrency = defineComponent<ChangeLanguageCurrencyProps>({
  props: {
    isShowIcon: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const staticTableDataStore = useStaticTableDataStore()
    const userInfoStore = useUserInfoStore()
    const languageStore = useLanguageStore()
    const assetsStore = useAssetsStore()
    const visible = ref(false)
    const { t } = useI18n()
    const searchLangValue = ref('')
    const searchCurrencyValue = ref('')

    const baseInfoStore = useBaseInfoStore()

    const langList = computed(() => {
      return staticTableDataStore.langList.filter((item) => {
        if (!searchLangValue.value) return true
        const langName = getLangName(item.langName)
        if (!langName) return false
        return langName.toLowerCase().includes(searchLangValue.value.toLowerCase())
      })
    })

    const currencyList = computed(() => {
      return staticTableDataStore.currencyList.filter((item) => {
        if (!searchCurrencyValue.value) return true
        return item.logicName.toLowerCase().includes(searchCurrencyValue.value.toLowerCase())
      })
    })

    const onOpen = () => {
      visible.value = true
    }

    const onCurrencyChange = (item: ApiResponse.StaticCurrency) => {
      if (!baseInfoStore.hasCurrency(item.logicName as keyof ApiResponse.OtcCoinRate)) {
        UiUtils.showWarning('该法币汇率暂未上线')
        visible.value = false
        return
      }
      userInfoStore.setToCurrency(item.logicName as CurrentCurrency<keyof ApiResponse.OtcCoinRate>)
      visible.value = false
      assetsStore.refresh()
    }

    const onLanguageChange = (item: ApiResponse.StaticLangI18n) => {
      languageStore.setCurrentLanguage(item.langName)
      visible.value = false
    }

    return () => (
      <div class="change-language-currency-layout">
        <ElPopover trigger={'click'} width={'fit-content'} placement={'bottom-end'} showArrow={false}>
          {{
            reference: () => (
              <div class={'current-language'} onClick={onOpen}>
                {props.isShowIcon ? (
                  <div class="language-icon">
                    <LanguageIcon isAnimated={languageStore.loading} />
                  </div>
                ) : null}
                <ElSpace>
                  <div class="lang-item-name">
                    <span>{getLangName(languageStore.currentLanguage)}</span>
                    <HocElDivider class="divider-line" direction="vertical" />
                    {userInfoStore.currentToCurrency.toLocaleUpperCase()}
                  </div>
                </ElSpace>
              </div>
            ),
            default: () => (
              <div class="lang-popover-content-layout lang-popover-content">
                <div class="item">
                  <div class="tab">{t('语言')}</div>
                  <InputView v-model={searchLangValue.value} class={'search-input'} placeholder={t('搜索语言')} />
                  <div class="lang-layout lang-list">
                    {langList.value.map((item) => {
                      const langName = getLangName(item.langName)
                      if (!langName) return null
                      return (
                        <div
                          class={classNames('lang-item', {
                            active: item.langName === languageStore.currentLanguage,
                          })}
                          key={item.langName}
                          onClick={() => onLanguageChange(item)}
                        >
                          <div class="lang-item-name">
                            <span>{langName}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div class="item">
                  <div class="tab">{t('法币')}</div>
                  <InputView v-model={searchCurrencyValue.value} class={'search-input'} placeholder={t('搜索法币')} />
                  <div class="lang-layout lang-list">
                    {currencyList.value.map((item) => (
                      <div
                        class={classNames('lang-item', {
                          active: item.logicName === userInfoStore.currentToCurrency,
                        })}
                        key={item.logicName}
                        onClick={() => onCurrencyChange(item)}
                      >
                        <div class="lang-item-name">
                          <span class="lang-item-name-currency">
                            {item.mark}
                            &nbsp;
                            {item.abbreviate}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ),
          }}
        </ElPopover>
      </div>
    )
  },
})
